import React, { useState, useEffect, useRef } from "react";
import "../Styling/ProposalBot.css";
import { useNavigate } from "react-router-dom";
import Navbar from "../Components/Navbar";

const ProposalBot = () => {
  const navigate = useNavigate();

  const [chatData, setChatData] = useState({
    email: "",
    service: "",
    name: "",
    companyName: "",
    companyIntro: "",
    location: "",
    q1: "",
    q2: "",
    finalQuestion: "",
  });
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [currentStep, setCurrentStep] = useState("name");
  const [showChat, setShowChat] = useState(false);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [isComplete, setIsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const messagesEndRef = useRef(null);
  const API_URL =
    "https://flask-hello-world-ruddy-chi-59.vercel.app/generate-proposal-info";

  const serviceQuestions = {
    "AI Marketing System": {
      q1: "How do you currently get clients?",
      q2: "What's the main thing you want this system to fix in the next 90 days?",
    },
    "Website Development": {
      q1: "What is the purpose of your website?",
      q2: "Do you have a site already? If yes, what's not working?",
    },
    "SEO / Organic Growth": {
      q1: "What do you want to rank for?",
      q2: "What is your main SEO goal?",
    },
    "E-commerce Growth System": {
      q1: "Which platform are you using? (Shopify, WooCommerce, etc.)",
      q2: "What is your main bottleneck right now?",
    },
    "Software / SaaS Development": {
      q1: "What type of software are you looking to build?",
      q2: "What are the core workflows or features you need?",
    },
  };
  const serviceToBackendMap = {
    "AI Marketing System": {
      project_type: "marketing",
      marketing_type: "ai_marketing",
    },
    "Website Development": {
      project_type: "marketing",
      marketing_type: "website",
    },
    "SEO / Organic Growth": {
      project_type: "marketing",
      marketing_type: "seo",
    },
    "E-commerce Growth System": {
      project_type: "marketing",
      marketing_type: "ecommerce",
    },
    "Software / SaaS Development": {
      project_type: "software",
    },
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const addBotMessage = (text) => {
    setMessages((prev) => [...prev, { type: "bot", text }]);
  };
  const addBotMessageWithTyping = (text, delay = 800) => {
    setIsBotTyping(true);

    setTimeout(() => {
      setIsBotTyping(false);
      addBotMessage(text);
    }, delay);
  };

  const addUserMessage = (text) => {
    setMessages((prev) => [...prev, { type: "user", text }]);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    const service = e.target.service.value;

    setChatData((prev) => ({ ...prev, email, service }));
    setShowChat(true);

    addBotMessageWithTyping(
      `Hi! I'm <strong>ProposalBot</strong>, your dedicated growth advisor. 🚀<br><br>I'll be gathering some information from you to create the perfect tailored proposal. This will only take a few minutes, and your insights will help us understand exactly what you need.<br><br>Let's get started!`
    );

    setTimeout(() => {
      addBotMessageWithTyping("First things first - what's your name?");
    }, 1500);
  };

  const buildUserDescription = () => {
    const questions = serviceQuestions[chatData.service];
    return `
Hi, I am ${chatData.name}, representing ${chatData.companyName}.
Company Introduction: ${chatData.companyIntro}
Location: ${chatData.location}

Service Interested In: ${chatData.service}

${questions.q1}
Answer: ${chatData.q1}

${questions.q2}
Answer: ${chatData.q2}

Current Situation & Goals: ${chatData.finalQuestion}
    `.trim();
  };

  const generateProposal = async () => {
    setIsLoading(true);
    addBotMessageWithTyping(
      "Perfect! Let me generate your personalized proposal..."
    );

    const userDescription = buildUserDescription();

    const serviceConfig = serviceToBackendMap[chatData.service];

    const payload = {
      email: chatData.email,
      user_description: userDescription,
      project_type: serviceConfig.project_type,
      ...(serviceConfig.project_type === "marketing" && {
        marketing_type: serviceConfig.marketing_type,
      }),
    };

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsLoading(false);
        setShowSuccess(true);

        addBotMessageWithTyping(
          `Thank you so much, ${chatData.name}! 🙌<br><br>Your customized proposal has been generated and sent to <strong>${chatData.email}</strong>.<br><br>You’ll be redirected to your dashboard shortly... 🚀`,
          1000
        );

        setTimeout(() => {
          navigate("/dashboard");
        }, 4000); // ⏱ stays visible for 4 seconds
      } else {
        throw new Error(data.error || "Failed to generate proposal");
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
      addBotMessageWithTyping(
        `❌ Sorry, something went wrong: ${error.message}. Please try again.`
      );
    }
  };

  const processUserInput = (message) => {
    setTimeout(() => {
      switch (currentStep) {
        case "name":
          setChatData((prev) => ({ ...prev, name: message }));
          addBotMessageWithTyping(
            `Great to meet you, ${message}! 👋<br><br>Now, what's your company name and could you give me a brief introduction about what your company does?`
          );
          setCurrentStep("company");
          break;

        case "company":
          const companyName = message.split("\n")[0] || message;
          setChatData((prev) => ({
            ...prev,
            companyName,
            companyIntro: message,
          }));
          addBotMessageWithTyping(
            `Excellent! ${companyName} sounds interesting.<br><br>Where is your company located?`
          );
          setCurrentStep("location");
          break;

        case "location":
          setChatData((prev) => ({ ...prev, location: message }));
          addBotMessageWithTyping(
            `Perfect! Now let's dive into some specifics about <strong>${
              chatData.service
            }</strong>.<br><br>${serviceQuestions[chatData.service].q1}`
          );
          setCurrentStep("q1");
          break;

        case "q1":
          setChatData((prev) => ({ ...prev, q1: message }));
          addBotMessageWithTyping(serviceQuestions[chatData.service].q2);
          setCurrentStep("q2");
          break;

        case "q2":
          setChatData((prev) => ({ ...prev, q2: message }));
          addBotMessageWithTyping(
            `Almost there, ${chatData.name}! 🎯<br><br>Last question: In 1-3 sentences, describe your current situation and what you'd ideally like to achieve.`
          );
          setCurrentStep("final");
          break;

        case "final":
          setChatData((prev) => ({ ...prev, finalQuestion: message }));
          setIsComplete(true);
          setCurrentStep("complete");
          generateProposal();
          break;

        default:
          break;
      }
    }, 800);
  };

  const handleSendMessage = () => {
    const message = userInput.trim();
    if (message === "" || isComplete) return;

    addUserMessage(message);
    setUserInput("");
    processUserInput(message);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSendMessage();
    }
  };

  return (
    <>
      <Navbar></Navbar>
      <div className="proposal-bot-main-container">
        <div className="proposal-bot-header-section">
          <h1>Let's Create Your Perfect Solution</h1>
          <p>
            Share your vision, and we'll craft a tailored proposal just for you
          </p>
        </div>

        {!showChat && (
          <div className="proposal-bot-form-card">
            <h3 className="mb-4 proposal-bot-form-title">
              Tell Us About Your Needs
            </h3>
            <form onSubmit={handleFormSubmit}>
              <div className="mb-4">
                <label
                  htmlFor="email"
                  className="form-label proposal-bot-form-label"
                >
                  Email Address
                </label>
                <input
                  type="email"
                  className="form-control proposal-bot-form-control"
                  id="email"
                  required
                  placeholder="your@email.com"
                />
              </div>
              <div className="mb-4">
                <label
                  htmlFor="service"
                  className="form-label proposal-bot-form-label"
                >
                  Which Service Interests You?
                </label>
                <select
                  className="form-select proposal-bot-form-select"
                  id="service"
                  required
                >
                  <option value="">Select a service...</option>
                  <option value="AI Marketing System">
                    AI Marketing System
                  </option>
                  <option value="Website Development">
                    Website Development
                  </option>
                  <option value="SEO / Organic Growth">
                    SEO / Organic Growth
                  </option>
                  <option value="E-commerce Growth System">
                    E-commerce Growth System
                  </option>
                  <option value="Software / SaaS Development">
                    Software / SaaS Development
                  </option>
                </select>
              </div>
              <div className="text-center">
                <button
                  type="submit"
                  className="btn btn-primary proposal-bot-btn-primary"
                >
                  Start the Conversation →
                </button>
              </div>
            </form>
          </div>
        )}

        {showChat && (
          <div className="proposal-bot-chat-container">
            <div className="proposal-bot-chat-header">
              <h3>💬 ProposalBot - Your Growth Advisor</h3>
              <small>
                <span className="proposal-bot-service-badge">
                  {chatData.service}
                </span>
              </small>
            </div>
            <div className="proposal-bot-chat-messages">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`proposal-bot-message ${
                    msg.type === "bot"
                      ? "proposal-bot-bot-message"
                      : "proposal-bot-user-message"
                  }`}
                >
                  {msg.type === "bot" ? (
                    <>
                      <div className="proposal-bot-bot-avatar">PB</div>
                      <div
                        className="proposal-bot-bot-text"
                        dangerouslySetInnerHTML={{ __html: msg.text }}
                      />
                    </>
                  ) : (
                    <div className="proposal-bot-user-text">{msg.text}</div>
                  )}
                </div>
              ))}

              {/* ✅ Bot typing indicator */}
              {isBotTyping && (
                <div className="proposal-bot-message proposal-bot-bot-message">
                  <div className="proposal-bot-bot-avatar">PB</div>
                  <div className="proposal-bot-bot-text typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="proposal-bot-chat-input-area">
              <div className="proposal-bot-input-group">
                <input
                  type="text"
                  className="form-control proposal-bot-chat-input"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your answer here..."
                  disabled={isComplete}
                />
                <button
                  className="proposal-bot-send-btn"
                  onClick={handleSendMessage}
                  disabled={isComplete}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProposalBot;
